import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { menuPanelClass } from 'features/playground/styles';

/**
 * PopMenu: a menu anchored under its trigger and portaled to <body>, so a
 * scrolling column or an overflow-hidden card never clips it.
 *
 *   renderTrigger({ ref, onClick, open, ariaProps }) -> node
 *   children(close) -> menu rows (use menuItemClass)
 *
 * Esc and an outside press close it; arrow keys move between rows.
 */
export default function PopMenu({ renderTrigger, children, width = 176, align = 'right', label }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const close = useCallback(() => setOpen(false), []);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const t = triggerRef.current?.getBoundingClientRect();
      if (!t) return;
      const margin = 8;
      const raw = align === 'right' ? t.right - width : t.left;
      setPos({
        top: Math.min(window.innerHeight - 8, t.bottom + 4),
        left: Math.max(margin, Math.min(window.innerWidth - width - margin, raw)),
      });
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, width, align]);

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (menuRef.current?.contains(e.target) || triggerRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') { setOpen(false); triggerRef.current?.focus?.(); }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    const first = menuRef.current?.querySelector('button:not([disabled])');
    first?.focus();
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const onMenuKey = (e) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const items = Array.from(menuRef.current?.querySelectorAll('button:not([disabled])') || []);
    if (!items.length) return;
    const i = items.indexOf(document.activeElement);
    const next = e.key === 'ArrowDown' ? (i + 1) % items.length : (i - 1 + items.length) % items.length;
    items[next].focus();
  };

  return (
    <>
      {renderTrigger({
        ref: triggerRef,
        onClick: () => setOpen((o) => !o),
        open,
        ariaProps: { 'aria-haspopup': 'menu', 'aria-expanded': open },
      })}
      {open && createPortal(
        <div
          ref={menuRef}
          role="menu"
          aria-label={label}
          onKeyDown={onMenuKey}
          className={`fixed z-[10000] ${menuPanelClass}`}
          style={{ top: pos.top, left: pos.left, width, fontFamily: 'Inter, sans-serif' }}
        >
          {children(close)}
        </div>,
        document.body,
      )}
    </>
  );
}
